import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_DOCUMENTTEMPLATE } from "../../../../utils/constants";
import { DocumentTemplateInterface } from "../../../../models/ProcurementModule/DocumentTemplate";
import documentTemplateService from "../../../../services/general/documentTemplateService";

interface UpdateDataContext {
  previousDocumentTemplate: DocumentTemplateInterface[];
}

const useUpdateDocumentTemplate = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    DocumentTemplateInterface,
    Error,
    DocumentTemplateInterface,
    UpdateDataContext
  >({
    mutationFn: async (documentTemplate: DocumentTemplateInterface) => {
      // Assuming the ID is stored in the company object or can be derived
      const { id, ...data } = documentTemplate;
      return documentTemplateService.Update(id || "", data);
    },

    onMutate: async (updatedDocumentTemplate: DocumentTemplateInterface) => {
      await queryClient.cancelQueries({
        queryKey: [
          CACHE_KEY_DOCUMENTTEMPLATE,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
      });

      const previousDocumentTemplate =
        queryClient.getQueryData<DocumentTemplateInterface[]>([
          CACHE_KEY_DOCUMENTTEMPLATE,
          page,
          pageSize,
        ]) || [];

      queryClient.setQueryData<DocumentTemplateInterface[]>(
        [
          CACHE_KEY_DOCUMENTTEMPLATE,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (documentTemplates = []) =>
          documentTemplates.map((data) =>
            data.id === updatedDocumentTemplate.id
              ? updatedDocumentTemplate
              : data
          )
      );

      return { previousDocumentTemplate };
    },

    onSuccess: (savedDepartment) => {
      queryClient.setQueryData<DocumentTemplateInterface[]>(
        [
          CACHE_KEY_DOCUMENTTEMPLATE,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (documentTemplates) =>
          documentTemplates?.map((data) =>
            data.id === savedDepartment.id ? savedDepartment : data
          )
      );
    },

    onError: (error, updatedDocumentTemplate, context) => {
      if (!context) return;

      queryClient.setQueryData<DocumentTemplateInterface[]>(
        [
          CACHE_KEY_DOCUMENTTEMPLATE,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousDocumentTemplate
      );
    },
  });
};

export default useUpdateDocumentTemplate;
